import { Box, MantineProvider, createStyles } from "@mantine/core";
import { Flex, IconV2, RpiLink, RpiText } from "@rpi/openapi-core";
import React from "react";
import { Link, Outlet, useMatches } from "react-router-dom";
import { useAuthUser } from "../../hooks/useAuthUser";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { RpiNotification, useRpiNotifications } from "../../hooks/useRpiNotifications";
import { canViewPage } from "../../utils/auth.util";
import { Error401View } from "../../views/error/Error401View";
import { DashboardLayoutNavbar } from "./DashboardLayoutNavbar";
import { optObj } from "../../utils/misc.util";
import { Toggle, tokens } from "@blurb/codex-react";
import { rpaTheme } from "../../theme/rpaTheme";
import { Environment } from "../../env/Environment";
import { NavBar } from "./NavBar";
import { newDashboard } from "../../views/dashboard/DashboardView";

/**
 * @featureFlag NewSandboxExperience
 * @description When active, use the new v2 sandbox banner design with rpaTheme styling
 */
// Set Feature Flag if this is a Vercel preview and it hasn't been set
if (window.location.hostname.includes("vercel.app") && localStorage.getItem("newSandboxExperience") === null) {
  localStorage.setItem("newSandboxExperience", "true");
}
export const newSandboxExperience = localStorage.getItem("newSandboxExperience") === "true";

export const DashboardLayout: React.FC = () => {
  const user = useAuthUser();
  const matches = useMatches();
  const { notifications } = useRpiNotifications();

  const showPaymentNotification = React.useMemo(
    () => !newDashboard && notifications[RpiNotification.PAYMENT_METHOD],
    [notifications]
  );
  const hasTaxNotification = React.useMemo(
    () => notifications[RpiNotification.TAX_DOCUMENT_NOT_UPLOADED],
    [notifications]
  );

  const children = React.useMemo(() => {
    if (!canViewPage(matches, user.cognitoUser)) return <Error401View root={false} hideButton />;
    return <Outlet />;
  }, [matches, user.cognitoUser]);

  return (
    <Flex.Column
      sx={(theme) => ({
        width: "100%",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.colors.sky[5],
        margin: 0,
        padding: 0,
      })}
    >
      {newSandboxExperience && !Environment.isSandbox && (
        <MantineProvider theme={rpaTheme}>
          <SandboxBanner isSandbox={user.isSandbox} setIsSandbox={user.setIsSandbox} />
        </MantineProvider>
      )}
      <Banner active={showPaymentNotification}>
        <IconV2.ErrorOutline color="currentColor" size={16} />
        <RpiText type="p3" weight="regular" children="Please submit a payment method before proceeding." />
        {user.is.CUSTOMER_ADMIN && (
          <RpiLink
            component={Link}
            to={getRoutePath("payment-method:create")}
            sx={{ color: "currentcolor" }}
            children="Add card details"
          />
        )}
      </Banner>

      <Banner active={hasTaxNotification && !showPaymentNotification}>
        <IconV2.ErrorOutline color="currentColor" size={16} />
        <RpiText
          type="p3"
          weight="regular"
          children="With your state reseller's certificate, no sales tax is charged on your orders."
        />
        <RpiLink
          component={Link}
          to={getRoutePath("billing")}
          sx={{ color: "currentcolor" }}
          children="Upload it here"
        />
      </Banner>

      <Flex.Row sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {newDashboard ? <NavBar /> : <DashboardLayoutNavbar />}
        <Box
          sx={(theme) => ({
            flex: 1,
            position: "relative",
            background: theme.colors.sky[5],
            marginLeft: newDashboard ? 80 : 0,
          })}
          children={children}
        />
      </Flex.Row>
    </Flex.Column>
  );
};

interface BannerProps {
  active?: boolean;
  border?: boolean;
  children?: React.ReactNode;
}

const Banner: React.FC<BannerProps> = ({ active, border, children }) => {
  return (
    <Flex.Row
      fullWidth
      justify="center"
      align="center"
      gap={16}
      sx={(theme) => ({
        background: theme.colors.spark[2],
        color: theme.colors.spark[6],
        height: 47,
        maxHeight: active ? 47 : 0,
        transition: "max-height ease-in-out 125ms",
        borderBottom: optObj(border, `1px solid ${theme.colors.spark[3]}`),
      })}
    >
      {active && children}
    </Flex.Row>
  );
};

const useSandboxBannerStyles = createStyles(() => ({
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]} ${tokens.spacing[6]} ${tokens.spacing[4]}`,
    width: "100%",
  },
  bannerSandbox: {
    backgroundColor: "#D9D9D9",
    color: tokens.color.semantic.text.bold,
  },
  bannerProduction: {
    backgroundColor: tokens.color.primitive.light.fog[950],
    color: tokens.color.semantic.text.inverse,
  },
  toggleContainer: {
    flexDirection: "row",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
    gap: tokens.spacing[1],
    backgroundColor: tokens.color.primitive.light.charcoal[50],
    borderRadius: tokens.radius.sm,
    border: `1px solid ${tokens.color.primitive.light.charcoal[500]}`,
    color: tokens.color.semantic.text.bold,
  },
}));

type SandboxBannerProps = {
  isSandbox: boolean;
  setIsSandbox: (value: boolean) => void;
};
const SandboxBanner: React.FC<SandboxBannerProps> = ({ isSandbox, setIsSandbox }) => {
  const { classes } = useSandboxBannerStyles();
  const getSandboxBannerMessage = (isSandbox: boolean): string => {
    const mode = isSandbox ? "Sandbox" : "Production";
    const description = isSandbox ? "your place to experiment with orders" : "all orders and payments are live!";

    return `You're now in ${mode} - ${description}`;
  };
  return (
    <Box className={`${classes.banner} ${isSandbox ? classes.bannerSandbox : classes.bannerProduction}`}>
      <RpiText type="p3" weight="bold">
        {isSandbox ? "SANDBOX" : "PRODUCTION"}
      </RpiText>
      <RpiText type="p3" weight="regular">
        {getSandboxBannerMessage(isSandbox)}
      </RpiText>

      <Box className={classes.toggleContainer} aria-label="Toggle between Sandbox and Production environments">
        <RpiText type="p3" weight="bold" id="sandbox-label">
          Sandbox
        </RpiText>
        <Toggle
          pressed={!isSandbox}
          onPressedChange={() => setIsSandbox(!isSandbox)}
          aria-label="Switch between Sandbox and Production mode"
          aria-describedby="sandbox-label production-label"
        />
        <RpiText type="p3" weight="bold" id="production-label">
          Production
        </RpiText>
      </Box>
    </Box>
  );
};
